import { config } from '../config';

/**
 * URL absolue du endpoint track. Ne renvoie jamais un chemin relatif seul (évite d’appeler le port du front par erreur).
 */
function buildTrackUrl(): string {
    const base = (config.api.baseUrl || '').trim().replace(/\/+$/, '');
    if (!base) {
        return '';
    }
    if (/\/api\/v1$/.test(base) || /\/v1$/.test(base)) {
        return `${base}/ui-button-stats/track`;
    }
    return `${base}/api/v1/ui-button-stats/track`;
}

/** Clés prédéfinies — alignées sur l’ordre produit (auth → home → interview → mapping). */
export const UI_BUTTON_EVENTS = {
    auth: {
        loginSubmit: 'auth.login.submit',
        loginResendVerification: 'auth.login.resend_verification',
        loginForgotPasswordLink: 'auth.login.forgot_password_link',
        loginSignupLink: 'auth.login.signup_link',
        signupSubmit: 'auth.signup.submit',
        signupMicrosoft: 'auth.signup.microsoft',
        signupLoginLink: 'auth.signup.login_link',
        signupReferralLink: 'auth.signup.referral_link',
        resetRequestSubmit: 'auth.reset.request_submit',
        resetRequestSignupLink: 'auth.reset_request.signup_link',
        resetNewPasswordSubmit: 'auth.reset.new_password_submit',
        resetNewPasswordLoginLink: 'auth.reset_new_password.login_link',
    },
    home: {
        quickDataCollect: 'home.quick.data_collect',
        quickCreateAi: 'home.quick.create_ai',
        quickCreateHuman: 'home.quick.create_human',
        quickCompanyAnalysis: 'home.quick.company_analysis',
        quickAutomate: 'home.quick.automate',
        quickInvited: 'home.quick.invited',
        myProcesses: 'home.section.my_processes',
        startTutorial: 'home.tutorial.start',
        /** Clic sur une ligne « processus recents » (ouvrir l'entretien). */
        recentOpenProcess: 'home.recent.open_process',
        /** Clic sur une ligne « collecte recente » (ouvrir la collection). */
        recentOpenDataCollection: 'home.recent.open_data_collection',
        /** Clic sur le menu ⋯ d’une ligne (organiser, renommer, etc.). */
        recentRowMenu: 'home.recent.row_menu',
        /** Recherche sur l’accueil — ouvrir un processus (carte). */
        searchOpenResult: 'home.search.open_result',
        /** Recherche sur l’accueil — menu ⋯ d’une carte. */
        searchRowMenu: 'home.search.row_menu',
        /**
         * Barre supérieure (HeaderLayout) : uniquement sur l’écran d’accueil produit
         * (`/home` ou `/process/overview` — même composant `Home`) pour ne pas mélanger
         * avec les mêmes contrôles sur d’autres routes.
         */
        topbarLogo: 'home.topbar.logo',
        topbarProcessesShortcut: 'home.topbar.processes_shortcut',
        topbarInvite: 'home.topbar.invite',
        topbarTheme: 'home.topbar.theme',
        topbarLangOpen: 'home.topbar.lang_open',
        topbarHelpOpen: 'home.topbar.help_open',
        topbarNewProcess: 'home.topbar.new_process',
        topbarUserMenu: 'home.topbar.user_menu',
        /** Sous-entrées du menu compte (même règle : overview uniquement). */
        topbarUserMenuCredits: 'home.topbar.user_menu_credits',
        topbarUserMenuInviteOwner: 'home.topbar.user_menu_invite_owner',
        topbarUserMenuApprovalRequests: 'home.topbar.user_menu_approval_requests',
        topbarUserMenuChangeCompany: 'home.topbar.user_menu_change_company',
        topbarUserMenuDashboard: 'home.topbar.user_menu_dashboard',
        topbarUserMenuLogout: 'home.topbar.user_menu_logout',
        topbarSearchFocus: 'home.topbar.search_focus',
        topbarMobileNav: 'home.topbar.mobile_nav',
    },
    interview: {
        /** Saisie libre / zone de message (défaut) */
        chatSend: 'interview.chat.send',
        chatReplyYes: 'interview.chat.reply_yes',
        chatReplyNo: 'interview.chat.reply_no',
        contextOpenUploader: 'interview.context.open_uploader',
        contextSkip: 'interview.context.skip',
        contextFileSend: 'interview.context.send_with_file',
        feedbackThumbUp: 'interview.feedback.thumb_up',
        feedbackThumbDown: 'interview.feedback.thumb_down',
        feedbackDownComment: 'interview.feedback.down_comment',
        voiceStart: 'interview.voice.start',
        railNewDiscovery: 'interview.rail.new_discovery',
        railProcessLibrary: 'interview.rail.process_library',
        railExportTranscript: 'interview.rail.export_transcript',
        railShare: 'interview.rail.share',
        topbarLangOpen: 'interview.topbar.lang_open',
        topbarHelpOpen: 'interview.topbar.help_open',
        topbarNewInterview: 'interview.topbar.new_interview',
        /** Vue entretien plein écran (ModernInterviewLayout) — panneau contributeurs / schémas, etc. */
        modernBack: 'interview.modern.back',
        modernAddContributor: 'interview.modern.add_contributor',
        modernToggleRoleGroup: 'interview.modern.toggle_role_group',
        modernSelectContributor: 'interview.modern.select_contributor',
        modernCollaboratorMenu: 'interview.modern.contributor_menu',
        modernCollaboratorReplace: 'interview.modern.contributor_replace',
        modernCollaboratorReminder: 'interview.modern.contributor_reminder',
        modernContributorDelete: 'interview.modern.contributor_delete',
        modernForceGenerate: 'interview.modern.force_generate',
        modernDiagramRowOpen: 'interview.modern.diagram_row_open',
        modernDiagramListMenu: 'interview.modern.diagram_list_menu',
        modernDiagramApprovalRequest: 'interview.modern.diagram_approval_request',
        modernDiagramRename: 'interview.modern.diagram_rename',
        modernDiagramShare: 'interview.modern.diagram_share',
        modernDiagramDelete: 'interview.modern.diagram_delete',
        modernShowAllDiagrams: 'interview.modern.show_all_diagrams',
        modernRunAnalysis: 'interview.modern.run_analysis',
        modernRailNotificationClick: 'interview.modern.rail_notification_click',
    },
    mapping: {
        topbarOpenInterview: 'mapping.topbar.open_interview',
        topbarCloseHome: 'mapping.topbar.close_to_home',
        topbarExportPdf: 'mapping.topbar.export_pdf',
        topbarExportPng: 'mapping.topbar.export_png',
        topbarZoomIn: 'mapping.topbar.zoom_in',
        topbarZoomOut: 'mapping.topbar.zoom_out',
        fileMakeCopy: 'mapping.file.make_copy',
        fileRename: 'mapping.file.rename',
        fileExportCsv: 'mapping.file.export_csv',
        fileExportXes: 'mapping.file.export_xes',
        fileExportTranscript: 'mapping.file.export_transcript',
        fileExportAttributesXls: 'mapping.file.export_attributes_xls',
        fileVersionSwitch: 'mapping.file.version_switch',
        fileApproveOpen: 'mapping.file.approve_modal',
        fileDisapproveOpen: 'mapping.file.disapprove_modal',
        fileShareOpen: 'mapping.file.share_modal',
        editUndo: 'mapping.edit.undo',
        editRedo: 'mapping.edit.redo',
        editCopy: 'mapping.edit.copy',
        editPaste: 'mapping.edit.paste',
        editSelectAll: 'mapping.edit.select_all',
        viewLayersBlocks: 'mapping.view.layers_blocks',
        viewLayersArrows: 'mapping.view.layers_arrows',
        viewWarnings: 'mapping.view.warnings',
        viewNotes: 'mapping.view.notes',
        viewPropertiesPanel: 'mapping.view.properties_panel',
        formatTextAlignLeft: 'mapping.format.text_align_left',
        formatTextAlignCenter: 'mapping.format.text_align_center',
        formatTextAlignRight: 'mapping.format.text_align_right',
        formatFontBold: 'mapping.format.font_bold',
        formatFontLight: 'mapping.format.font_light',
        formatTransformNone: 'mapping.format.text_transform_none',
        formatTransformUpper: 'mapping.format.text_transform_uppercase',
        formatTransformLower: 'mapping.format.text_transform_lowercase',
        formatTransformCapitalize: 'mapping.format.text_transform_capitalize',
        ownerLayerSettings: 'mapping.owner.layer_settings',
        ownerRequestApproval: 'mapping.owner.request_approval',
        ownerEnableMapEdit: 'mapping.owner.enable_map_edit',
        ownerEnableDataEdit: 'mapping.owner.enable_data_edit',
        helpReportBug: 'mapping.help.report_bug',
        panelOpenAnalysis: 'mapping.panel.open_analysis',
        propsBarZoomOut: 'mapping.props_bar.zoom_out',
        propsBarZoomIn: 'mapping.props_bar.zoom_in',
        shapeDragProcess: 'mapping.shape.drag_process',
        shapeDragDecision: 'mapping.shape.drag_decision',
        shapeDragStart: 'mapping.shape.drag_start',
        shapeDragEnd: 'mapping.shape.drag_end',
        shapeDragNote: 'mapping.shape.drag_note',
        /** Onglets feuilles (barre fixe bas) — pas le canevas. */
        sheetsTabSwitch: 'mapping.sheets.tab_switch',
        sheetsAdd: 'mapping.sheets.add',
        sheetsAllSheetsMenu: 'mapping.sheets.all_sheets_menu',
        sheetsScrollLeft: 'mapping.sheets.scroll_left',
        sheetsScrollRight: 'mapping.sheets.scroll_right',
        sheetsRename: 'mapping.sheets.rename',
        sheetsCopy: 'mapping.sheets.copy',
        sheetsMoveLeft: 'mapping.sheets.move_left',
        sheetsMoveRight: 'mapping.sheets.move_right',
        sheetsHide: 'mapping.sheets.hide',
        sheetsDelete: 'mapping.sheets.delete',
        sheetsShowHidden: 'mapping.sheets.show_hidden',
        sheetsFeedbackThumbsUp: 'mapping.sheets.feedback_thumbs_up',
        sheetsFeedbackThumbsDown: 'mapping.sheets.feedback_thumbs_down',
        /** Panneau d’analyse Bêta (ResizableLayout / analysis UI), hors canevas. */
        analysisClosePanel: 'mapping.analysis.close_panel',
        analysisMaximize: 'mapping.analysis.maximize',
        analysisMinimize: 'mapping.analysis.minimize',
        analysisOpenSavedReports: 'mapping.analysis.open_saved_reports',
        analysisNewAnalysis: 'mapping.analysis.new_analysis',
        analysisResizeHandle: 'mapping.analysis.resize_handle',
        analysisPromptSubmit: 'mapping.analysis.prompt_submit',
        analysisSuggestionClick: 'mapping.analysis.suggestion_click',
        analysisSaveAllConfirm: 'mapping.analysis.save_all_confirm',
        analysisReportSelect: 'mapping.analysis.report_select',
    },
    collect: {
        resultsOpenAnalysis: 'collect.results.open_analysis',
        synthesisGenerateDashboard: 'collect.synthesis.generate_dashboard',
    },
} as const;

/**
 * Envoie l’événement et attend la réponse — à utiliser avant un `navigate()` ou une action qui démonte la page.
 */
export async function trackUiButtonEventAsync(eventKey: string): Promise<void> {
    if (typeof window === 'undefined' || !eventKey?.trim()) return;
    const key = eventKey.trim().toLowerCase();
    const url = buildTrackUrl();
    if (!url) {
        if (config.app.debug) {
            // eslint-disable-next-line no-console
            console.warn(
                '[uiButtonStats] REACT_APP_API_BASE_URL manquant : impossible d’enregistrer le clic',
                key
            );
        }
        return;
    }
    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify({ event_key: key }),
            mode: 'cors',
            credentials: 'include',
            keepalive: true,
        });
        if (!res.ok && config.app.debug) {
            const txt = await res.text().catch(() => '');
            // eslint-disable-next-line no-console
            console.warn('[uiButtonStats]', res.status, url, txt?.slice(0, 300));
        }
    } catch (e) {
        if (config.app.debug) {
            // eslint-disable-next-line no-console
            console.warn('[uiButtonStats] fetch', url, e);
        }
    }
}

/** Version fire-and-forget (liens auth, etc.). */
export function trackUiButtonEvent(eventKey: string): void {
    void trackUiButtonEventAsync(eventKey);
}
